import React, { useCallback, useState, useEffect, useMemo, useRef } from 'react';
import {
    BrowserRouter as Router,
    Redirect
} from 'react-router-dom';
import _ from 'lodash';
// Imported First to allow Jutro styles to be overridden
import './App.scss';
import { setComponentMapOverrides } from '@jutro/legacy/uiconfig';
import { useBreakpoint } from '@jutro/layout';
import { browserSupport } from 'wmic-portals-browserblocking-js';
import { ModalNextProvider, ModalNextEmitter } from '@jutro/components';
import { useTranslator } from '@jutro/locale';
import { routeConfirmationModal } from '@jutro/router';
import { publish } from '@jutro/events';
// eslint-disable-next-line import/no-unresolved
import vmTranslator from '@xengage/gw-platform-translations';

import { ViewModelServiceFactory } from '@xengage/gw-portals-viewmodel-js';
import { AccurateBreakpointPropagation } from '@xengage/gw-jutro-adapters-react';
import { filterCapabilityRoutes } from '@xengage/gw-portals-config-js';
import {
    WMICCustomHeader,
    WMICCustomFooter,
    WMICErrorHandlingService,
    WMICESignatureCallbackPage,
    WMICQuickRetrieveQuotePage,
    WMICPaymentCallback
} from 'gw-capability-quoteandbind-common-react';

import {
    segmentationMap,
    segmentationComponentMap,
    Segmentation,
    SegmentationOption
} from 'gw-portals-segmentation-react';
import { policyJobComponentMap, policyJobComponents } from 'gw-capability-policyjob-react';
import { ViewModelServiceContext } from '@xengage/gw-portals-viewmodel-react';
import {
    platformComponents,
    platformComponentMap,
    ImageComponent
} from 'gw-components-platform-react';
import { ErrorBoundary } from '@xengage/gw-portals-error-react';
import { AddressLookupComponentMap, AddressLookupComponents } from 'gw-capability-address-react';
import { VehicleInfoLookupComponentMap, VehicleInfoLookupComponents } from 'gw-capability-vehicleinfo-react';
import { GooglePlacesLookupComponentMap, GooglePlacesLookupComponents } from 'gw-capability-googleplaces-react';
import { DependencyProvider } from '@xengage/gw-portals-dependency-react';
import { LoadSaveService, CustomQuoteService, MultiProductService } from 'gw-capability-quoteandbind';
import { PropertyCodeLookupService } from 'gw-capability-propertycode';
import { GTPWizard } from 'gw-capability-quoteandbind-gtp-react';
import { WMICOneIncPaymentService } from 'wmic-capability-quoteandbind';

import { BOPWizard } from 'gw-capability-quoteandbind-bop-react';
import { HOWizard, WMICKnockoutPage, WMICPolicyInProcessPage, WMICSuccessPage } from 'gw-capability-quoteandbind-ho-react';
import { PAWizard } from 'gw-capability-quoteandbind-pa-react';
import { SLQWizard } from 'gw-capability-quoteandbind-slq-react';
import FaqPage from 'gw-capability-faq-react';
import appConfig from 'app-config';

import { WMICApplicationUtil, APP_NAMES, WMICRemoteLogger, WMICFeatureFlagUtil, WMICTopics } from 'wmic-portals-utils-js';
import { AdobeAnalytics } from 'wmic-adobe-analytics-react';
import { setError, ERROR_REASONS } from '../utils/dataLayer';

import { WMICRegionUrlUtil } from 'wmic-portals-url-js';
import LandingPage from '../pages/LandingPage/LandingPage';

import WMICRetrievePage from '../pages/WMICRetrievePage/WMICRetrievePage';
import CustomLandingPage from '../pages/CustomLandingPage/CustomLandingPage';
import WMICContactUs from '../pages/WMICContactUs/WMICContactUs';
import appRoutes from './App.routes.metadata.json5';
import './App.messages';
import ApplicationHeader from '../components/ApplicationHeader/ApplicationHeader';

import { AppFloorPlan } from '@jutro/legacy/floorplan';

const modalEmitter = new ModalNextEmitter();

const { env, capabilitiesConfig } = appConfig;


const componentMap = {
    landingpage: LandingPage,
    retrievepage: WMICRetrievePage,
    gtpwizard: GTPWizard,
    bopwizard: BOPWizard,
    howizard: HOWizard,
    pawizard: PAWizard,
    slqwizard: SLQWizard,
    contactus: WMICContactUs,
    faqpage: FaqPage,
    successpage: WMICSuccessPage,
    knockoutpage: WMICKnockoutPage,
    signaturecallbackpage: WMICESignatureCallbackPage,
    quickretrievequote: WMICQuickRetrieveQuotePage,
    policyinprocess: WMICPolicyInProcessPage,
    paymentcallback: WMICPaymentCallback,
};

const renderCustomHeader = (props) => <ApplicationHeader {...props} />;

setComponentMapOverrides({
    ...platformComponentMap,
    ...AddressLookupComponentMap,
    ...VehicleInfoLookupComponentMap,
    ...GooglePlacesLookupComponentMap,
    ...segmentationMap,
    ...policyJobComponentMap,
    // replace the native IMG component with a proxied version
    img: { component: 'img' }
}, {
    ...platformComponents,
    ...AddressLookupComponents,
    ...VehicleInfoLookupComponents,
    ...GooglePlacesLookupComponents,
    ...segmentationComponentMap,
    ...policyJobComponents,
    img: ImageComponent
});

const App = () => {
    // eslint-disable-next-line no-unused-vars
    const translator = useTranslator();
    const [viewModelService, setViewModelService] = useState(undefined);
    const [redirectTarget, setRedirectTarget] = useState(undefined);
    
    const [logger, setLogger] = useState();
    const [featureFlags, setFeatureFlags] = useState(undefined);
    // Ref always holds the current logger instance, avoiding stale closures in async effects
    const loggerRef = useRef(null);
    

    const [regionLongName, setRegionLongName] = useState(WMICRegionUrlUtil.getRegionLongName());

    WMICApplicationUtil.setAppName(APP_NAMES.CE);
    
    useEffect(() => {
        if (window.self === window.top) {
            const theBody = document.getElementsByTagName('body')[0];
            theBody.className += ' wmicRoot';
        }
    }, [])

    // Create the logger once on first render (with empty flags so the app is never blocked).
    // On subsequent [logger] triggers (e.g. router re-renders) just update the location.
    useEffect(() => {
        if (!loggerRef.current) {
            const newLogger = WMICRemoteLogger.getWMICRemoteLogger(
                'App',
                '',
                window.location,
                {},
                null
            );
            loggerRef.current = newLogger;
            setLogger(newLogger);
            window.__wmicLogger = newLogger;
        } else {
            loggerRef.current.setLocation({ location: window.location });
        }
    }, [logger]);

    // Fetch feature flags on initial load (unauthenticated CE path).
    // Non-blocking: the app renders fully while this resolves.
    // Cancellation guard prevents setState on an unmounted component (race condition).
    useEffect(() => {
        let cancelled = false;
        WMICFeatureFlagUtil.useFlags({})
            .then((flags) => {
                if (cancelled) return;
                setFeatureFlags(flags);
                const featureFlagsTypes = WMICFeatureFlagUtil.getFeatureFlags();
                const serverDate = flags[featureFlagsTypes.RENTERSQNB]?.serverDate;
                if (serverDate) { window.__wmicServerDate = new Date(serverDate).getTime(); }
                // Re-initialize logger with real flags once they arrive.
                // loggerRef avoids stale closure — always points to the current instance.
                const updatedLogger = WMICRemoteLogger.getWMICRemoteLogger(
                    'App',
                    '',
                    window.location,
                    flags,
                    null
                );
                loggerRef.current = updatedLogger;
                setLogger(updatedLogger);
                window.__wmicLogger = updatedLogger;
            })
            .catch(() => { /* fail safe — app and logger continue with empty defaults */ });
        return () => { cancelled = true; };
    }, []);
    

    const { breakpoint } = useBreakpoint({});
    const getLogoSrc = useCallback(() => {
        let logoName = "logo-default.svg";
        if(breakpoint === 'phone' || breakpoint === 'phoneWide'){
            logoName = "logo-mobile.svg";
        }
        const prefix = _.get(env, 'DEPLOYMENT_PATH', '').replace(/\/$/, '');
        return `${prefix}/branding/${logoName}`;
    }, [breakpoint]);

    const floorPlansConfig = useMemo(() => {
        return [{
            className: "floorPlan",
            scrollContent: false,
            routes: filterCapabilityRoutes(capabilitiesConfig, appRoutes.routes),
            showHeader: false,
            showFooter: false
        }];
    }, []);

    const getRegion = () => {
        let region = WMICRegionUrlUtil.getRegionByParam(window.location.search);
        if(!region) {
            region = WMICRegionUrlUtil.getRegion();
        }
        setRegionLongName(WMICRegionUrlUtil.getLongNameFromRegion(region));
    }

    useEffect(() => {
        browserSupport();
        getRegion();
    }, []);

    useEffect(() => {
        const translatorFn = vmTranslator(translator);
        import(
            /* webpackChunkName: "product-metadata" */
            // eslint-disable-next-line import/no-unresolved
            'product-metadata'
        ).then((productMetadata) => {
            const { default: result } = productMetadata;
            const newViewmodeService = ViewModelServiceFactory.getViewModelService(result, translatorFn);
            setViewModelService(newViewmodeService);
            _.set(window, 'wmic.viewmodel.metadata', newViewmodeService.productMetadata);
        });

    }, [translator]);

    const handleError = useCallback((error = {}) => {
        // Guard against re-entrant calls (e.g. logger failure triggering handleError again)
        // Once we set a redirect, block all further error handling until navigation completes
        if (window.__wmicHandlingError) {
            console.warn('[App] handleError re-entry blocked - logger may be unavailable');
            return null;
        }
        window.__wmicHandlingError = true;

        try {
            // Wrap logger separately so a failed remote log doesn't re-trigger handleError
            try {
                if (logger) {
                    logger.error(error);
                }
            } catch (logErr) {
                console.error('[App] Remote logger failed (backend may be unavailable):', logErr);
            }

            // Track error in Adobe Analytics (existing event system)
            publish(WMICTopics.FORM_ERROR, {
                error: error.code || 'system error',           // eVar92
                error_message: error.message || error.code || 'Unknown error',
                page_name: window.location.pathname,
                quoteId: error.quoteID
            });

            // NEW: Track error in data layer with INS document formatting
            const displayMode = error.code || 'TechnicalError';
            
            // Map error codes to INS error format
            if (displayMode === 'TECHNICAL_ERROR' || displayMode === 'TechnicalError') {
                setError(ERROR_REASONS.TECHNICAL_ERROR, 'sorry technicalerror');
            } else if (displayMode === 'TECHNICAL_ERROR_CREATE_QUOTE') {
                setError(ERROR_REASONS.CREATE_QUOTE, 'sorry technicalerror');
            } else if (displayMode === 'TECHNICAL_ERROR_RETRIEVE_QUOTE') {
                setError(ERROR_REASONS.RETRIEVE_QUOTE, 'sorry technicalerror');
            } else if (displayMode === 'DECLINE') {
                // Map decline reason to specific error
                setError(ERROR_REASONS.UNDERWRITING_GUIDELINES, 'sorry qnb');
            } else if (displayMode === 'INVALID_SESSION') {
                setError(ERROR_REASONS.SESSION_TIMEOUT, 'sorry sessionexpired');
            } else {
                // Generic error for unmapped codes
                setError(ERROR_REASONS.TECHNICAL_ERROR, 'sorry technicalerror');
            }
            
            // Fire analytics with updated data layer
            if (window.utag && window.utag.view) {
                console.log('[App] Error tracked to Adobe Analytics:', {
                    error: window.wins_data_layer.error,
                    page: window.wins_data_layer.page,
                    errorCode: displayMode,
                    quoteId: error.quoteID,
                    dataLayer: window.wins_data_layer
                });
                window.utag.view(window.wins_data_layer);
            } else {
                console.warn('[App] Adobe Analytics (utag) not available - error not tracked!');
            }

            const target = {
                pathname: `/contact-us/${displayMode}`,
                state: {
                    quoteID: error.quoteID,
                    declineMessages: error.declineMessages,
                    residenceType: error.residenceType
                }
            };
            setRedirectTarget(target);
            // NOTE: intentionally NOT clearing window.__wmicHandlingError here.
            // The flag stays true until the page navigates away, preventing repeated
            // calls from logger retries while the redirect is pending.
        } catch (err) {
            console.error('[App] Error in handleError:', err);
            setRedirectTarget({ pathname: '/contact-us/TechnicalError' });
            // Do not clear flag here either - redirect is pending
        }
        return null;
    }, [logger]);

    return (
        <AccurateBreakpointPropagation>
            {/* NEW: Adobe Analytics component for tracking page views and user actions */}
            <AdobeAnalytics 
                applicationName="W Insurance Quote and Buy"
                productType="Renters Insurance Quote"
            />
            <DependencyProvider value={{
                LoadSaveService,
                CustomQuoteService,
                MultiProductService,
                PropertyCodeLookupService,
                WMICErrorHandlingService,
                getRegion,
                WMICOneIncPaymentService
            }}
            >
                <ViewModelServiceContext.Provider value={viewModelService}>
                    <Segmentation type="query-params">
                        <SegmentationOption defaultOption>
                            <Router
                                basename="/quote-and-buy"
                                getUserConfirmation={(message, callback) => {
                                    if (redirectTarget || (typeof window !== 'undefined' && window.__wmicSkipRouteConfirm)) {
                                        if (typeof window !== 'undefined') {
                                            window.__wmicSkipRouteConfirm = false;
                                        }
                                        callback(true);
                                        return;
                                    }
                                    return routeConfirmationModal(message, callback, modalEmitter);
                                }}>
                                <ModalNextProvider>
                                    <WMICCustomHeader />
                                    <div className="main-wrapper qnbContent">
                                        {redirectTarget && <Redirect to={redirectTarget} />}
                                        <ErrorBoundary onError={handleError}>
                                            <AppFloorPlan
                                                componentMap={componentMap}
                                                floorPlans={floorPlansConfig}
                                            />
                                        </ErrorBoundary>
                                    </div>
                                    <WMICCustomFooter regionLongName={regionLongName} />
                                </ModalNextProvider>
                            </Router>
                        </SegmentationOption>
                        <SegmentationOption is={[{ LP: '1' }, { LP: '2' }]}>
                            <CustomLandingPage />
                        </SegmentationOption>
                    </Segmentation>
                </ViewModelServiceContext.Provider>
            </DependencyProvider>
        </AccurateBreakpointPropagation>
    );
};

export default App;
